import { useEffect, useState, useMemo } from "react";
import { teamApi } from "@/lib/api";
import { Users, Plus, Pencil, Trash2, Phone, Mail, User } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export const DEPARTMENTS = [
  "PRODUCTION",
  "CAMERA",
  "LIGHTING",
  "GRIP",
  "SOUND",
  "ART",
  "WARDROBE",
  "HAIR & MAKEUP",
  "TALENT",
  "POST",
  "OTHER",
];

const blankForm = {
  name: "",
  role: "",
  department: "PRODUCTION",
  member_type: "crew",
  company: "",
  phone: "",
  email: "",
  notes: "",
};

export default function TeamPage() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankForm);
  const [filter, setFilter] = useState("all"); // all | crew | talent

  const load = async () => {
    setLoading(true);
    try {
      setTeam(await teamApi.list());
    } catch {
      toast.error("Failed to load team");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const visible = useMemo(() => {
    if (filter === "all") return team;
    return team.filter((m) => m.member_type === filter);
  }, [team, filter]);

  const grouped = useMemo(() => {
    if (filter === "talent") {
      return [{ dept: "TALENT", items: visible }];
    }
    if (filter === "client") {
      return [{ dept: "CLIENT", items: visible }];
    }
    const map = {};
    visible.forEach((m) => {
      let dept;
      if (m.member_type === "talent") dept = "TALENT";
      else if (m.member_type === "client") dept = "CLIENT";
      else dept = m.department || "OTHER";
      if (!map[dept]) map[dept] = [];
      map[dept].push(m);
    });
    const order = [...DEPARTMENTS, "CLIENT", "OTHER"];
    return order
      .filter((d) => map[d] && map[d].length)
      .map((d) => ({ dept: d, items: map[d] }));
  }, [visible, filter]);

  const openNew = () => {
    setEditing(null);
    setForm(blankForm);
    setDialogOpen(true);
  };

  const openEdit = (m) => {
    setEditing(m);
    setForm({
      name: m.name || "",
      role: m.role || "",
      department: m.department || "PRODUCTION",
      member_type: m.member_type || "crew",
      company: m.company || "",
      phone: m.phone || "",
      email: m.email || "",
      notes: m.notes || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    try {
      if (editing) {
        const updated = await teamApi.update(editing.id, form);
        setTeam((t) => t.map((x) => (x.id === updated.id ? updated : x)));
        toast.success(`Updated "${updated.name}"`);
      } else {
        const created = await teamApi.create(form);
        setTeam((t) => [...t, created]);
        toast.success(`Added "${created.name}"`);
      }
      setDialogOpen(false);
    } catch {
      toast.error("Save failed");
    }
  };

  const handleDelete = async (m) => {
    try {
      await teamApi.remove(m.id);
      setTeam((t) => t.filter((x) => x.id !== m.id));
      toast.success(`Removed "${m.name}"`);
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="min-h-screen max-w-screen-2xl mx-auto px-6 lg:px-10 py-10">
      <div className="flex items-center justify-between gap-4 mb-2 flex-wrap">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-5 h-5 text-amber-500" />
            <div className="text-overline text-zinc-500">Roster</div>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-light text-zinc-100">
            Team
          </h1>
          <p className="text-zinc-500 text-sm mt-2 max-w-lg">
            People who work with you — crew and talent. Build your roster once,
            reuse on every call sheet.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="inline-flex rounded-sm border border-zinc-800 bg-zinc-900 p-0.5">
            {[
              { v: "all", l: "All" },
              { v: "crew", l: "Crew" },
              { v: "talent", l: "Talent" },
              { v: "client", l: "Client" },
            ].map((f) => (
              <button
                key={f.v}
                data-testid={`filter-${f.v}`}
                onClick={() => setFilter(f.v)}
                className={`px-3 py-1.5 text-xs rounded-sm transition-all ${
                  filter === f.v
                    ? "bg-zinc-950 text-amber-500 border border-amber-500/30"
                    : "text-zinc-400 hover:text-zinc-100"
                }`}
              >
                {f.l}
              </button>
            ))}
          </div>
          {isAdmin && (
            <button
              data-testid="new-team-btn"
              onClick={openNew}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold bg-amber-500 text-black hover:bg-amber-400 rounded-sm transition-all"
            >
              <Plus className="w-4 h-4" /> Add Person
            </button>
          )}
        </div>
      </div>

      <div className="mt-10">
        {loading ? (
          <div className="text-zinc-500">Loading…</div>
        ) : team.length === 0 ? (
          <div className="border border-dashed border-zinc-800 rounded-md p-12 text-center">
            <Users className="w-7 h-7 text-amber-500 mx-auto mb-3" strokeWidth={1.5} />
            <h3 className="font-heading text-xl text-zinc-100 mb-1">
              Your roster is empty
            </h3>
            <p className="text-zinc-500 text-sm">
              Add directors, crew, talent &mdash; they&apos;ll be selectable on any call sheet.
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {grouped.map(({ dept, items }) => (
              <div key={dept}>
                <div className="text-overline text-zinc-400 mb-3">
                  {dept}{" "}
                  <span className="text-zinc-600 font-mono">· {items.length}</span>
                </div>
                <div className="border border-zinc-800 rounded-md overflow-hidden bg-zinc-900">
                  {items.map((m, i) => (
                    <div
                      key={m.id}
                      data-testid={`team-row-${m.id}`}
                      className={`group flex items-center gap-4 px-4 py-3 ${
                        i < items.length - 1 ? "border-b border-zinc-800/60" : ""
                      } hover:bg-zinc-800/30 transition-colors`}
                    >
                      <div className="w-8 h-8 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center text-zinc-400 text-xs font-medium shrink-0">
                        {initials(m.name)}
                      </div>
                      <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-4 gap-2 items-center">
                        <div className="min-w-0">
                          <div className="text-sm text-zinc-100 truncate">{m.name}</div>
                          {m.member_type === "talent" && (
                            <div className="text-overline text-amber-500/80 mt-0.5">
                              Talent
                            </div>
                          )}
                          {m.member_type === "client" && (
                            <div className="text-overline text-sky-400/80 mt-0.5">
                              {m.company || "Client"}
                            </div>
                          )}
                        </div>
                        <div className="text-sm text-zinc-400 truncate">
                          {m.role || <span className="text-zinc-600 italic">No role</span>}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-zinc-500">
                          {m.phone && (
                            <span className="flex items-center gap-1 font-mono">
                              <Phone className="w-3 h-3" />
                              {m.phone}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-zinc-500 truncate">
                          {m.email && (
                            <span className="flex items-center gap-1 truncate">
                              <Mail className="w-3 h-3 shrink-0" />
                              <span className="truncate">{m.email}</span>
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                        {isAdmin && (
                          <>
                            <button
                              data-testid={`edit-team-${m.id}`}
                              onClick={() => openEdit(m)}
                              className="p-1.5 rounded-sm text-zinc-500 hover:text-zinc-100 hover:bg-zinc-800"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <button
                                  data-testid={`delete-team-${m.id}`}
                                  className="p-1.5 rounded-sm text-zinc-500 hover:text-rose-400 hover:bg-zinc-800"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Remove &ldquo;{m.name}&rdquo;?</AlertDialogTitle>
                                  <AlertDialogDescription className="text-zinc-500">
                                    This won&apos;t affect call sheets they&apos;re already on.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel className="bg-transparent border-zinc-800 text-zinc-300 hover:bg-zinc-900">
                                    Cancel
                                  </AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDelete(m)}
                                    className="bg-rose-500 text-zinc-50 hover:bg-rose-600"
                                  >
                                    Remove
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-light">
              {editing ? "Edit Person" : "Add Person"}
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              Talent appears in the TALENT table on call sheets. Crew is grouped by
              department.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-3">
            <Field label="Name" required>
              <Input
                data-testid="team-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Gregory Norton"
                className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Type">
                <Select
                  value={form.member_type}
                  onValueChange={(v) => setForm({ ...form, member_type: v })}
                >
                  <SelectTrigger
                    data-testid="team-type-select"
                    className="bg-zinc-950 border-zinc-800 focus:ring-amber-500"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                    <SelectItem value="crew">Crew</SelectItem>
                    <SelectItem value="talent">Talent</SelectItem>
                    <SelectItem value="client">Client</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Department" hide={form.member_type === "talent" || form.member_type === "client"}>
                <Select
                  value={form.department}
                  onValueChange={(v) => setForm({ ...form, department: v })}
                >
                  <SelectTrigger
                    data-testid="team-dept-select"
                    className="bg-zinc-950 border-zinc-800 focus:ring-amber-500"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-h-72">
                    {DEPARTMENTS.map((d) => (
                      <SelectItem key={d} value={d}>
                        {d}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Role">
              <Input
                data-testid="team-role-input"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                placeholder={
                  form.member_type === "talent"
                    ? "Model, Lead Actor, Narrator…"
                    : form.member_type === "client"
                      ? "Brand Manager, Producer, Marketing Lead…"
                      : "Director / Prod., 1st AC, Wardrobe Stylist…"
                }
                className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
              />
            </Field>
            {form.member_type === "client" && (
              <Field label="Company Name">
                <Input
                  data-testid="team-company-input"
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  placeholder="Acme Corp, Nike, Target…"
                  className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
                />
              </Field>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Phone">
                <Input
                  data-testid="team-phone-input"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="(612) 716-2374"
                  className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
                />
              </Field>
              <Field label="Email">
                <Input
                  data-testid="team-email-input"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="name@studio.com"
                  className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
                />
              </Field>
            </div>
            <Field label="Notes">
              <Textarea
                data-testid="team-notes-input"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 min-h-[60px]"
              />
            </Field>
            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="bg-transparent border-zinc-800 text-zinc-300 hover:bg-zinc-900 hover:text-zinc-100"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="save-team-btn"
                className="bg-amber-500 text-black hover:bg-amber-400 rounded-sm"
              >
                {editing ? "Save Changes" : "Add Person"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, required, children, hide }) {
  if (hide) return <div />;
  return (
    <div className="space-y-1.5">
      <Label className="text-overline text-zinc-500">
        {label}
        {required && <span className="text-amber-500 ml-1">*</span>}
      </Label>
      {children}
    </div>
  );
}

function initials(name) {
  return (name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");
}
